import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

const STORAGE_KEY = 'duckdrop-emojified';

type EmojiContextValue = {
  isEmojified: boolean;
  setEmojified: (value: boolean) => void;
  toggleEmojified: () => void;
  emojify: (emoji: string, text: string) => string;
};

const EmojiContext = createContext<EmojiContextValue | null>(null);

export function getStoredEmojified(): boolean {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === null) {
      return true;
    }
    return stored === 'true';
  } catch {
    return true;
  }
}

export function EmojiProvider({ children }: { children: ReactNode }) {
  const [isEmojified, setEmojifiedState] = useState<boolean>(getStoredEmojified);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, String(isEmojified));
    } catch {
      // Ignore storage errors (private browsing, etc.).
    }
  }, [isEmojified]);

  const emojify = useCallback(
    (emoji: string, text: string) => (isEmojified ? `${emoji} ${text}` : text),
    [isEmojified]
  );

  const value = useMemo<EmojiContextValue>(
    () => ({
      isEmojified,
      setEmojified: setEmojifiedState,
      toggleEmojified: () => setEmojifiedState((current) => !current),
      emojify,
    }),
    [isEmojified, emojify]
  );

  return <EmojiContext.Provider value={value}>{children}</EmojiContext.Provider>;
}

export function useEmoji(): EmojiContextValue {
  const context = useContext(EmojiContext);
  if (!context) {
    throw new Error('useEmoji must be used within EmojiProvider');
  }
  return context;
}
